import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Layers, Plus, Sparkles, Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, useParams } from 'react-router';
import { canAny } from '../../api/client';
import {
  addCalendarRule,
  addGroupEvent,
  addGroupMembers,
  cancelGroupEvent,
  deleteCalendarRule,
  deleteGroup,
  fetchGroup,
  fetchGroups,
  fetchPrograms,
  removeGroupMembers,
  saveGroup,
} from '../../api/ops';
import { getDataSource } from '../../data';
import { Card, Field, Modal, PageHeader, PresenceBadge } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useMe } from '../auth/session';
import { ReportScopePicker, useReportScope } from '../reports/ReportScope';
import { ProgramsTab } from './ProgramsTab';
import { RulesTab } from './RulesTab';
import { ScenesTab } from './ScenesTab';

export const GROUP_KINDS: api.GroupKind[] = [
  'street',
  'intersection',
  'pedestrian_crossing',
  'parking',
  'pedestrian',
  'park',
  'festive',
  'other',
];
const RUN_MODES: api.RunMode[] = ['central', 'device', 'both', 'off'];
const TABS = [
  { key: 'groups', icon: Layers },
  { key: 'programs', icon: CalendarClock },
  { key: 'scenes', icon: Sparkles },
  { key: 'rules', icon: Zap },
] as const;

/** Working groups / operating scenarios, programs and scenes (AFM requirements b, h, o). */
export function GroupsPage() {
  const { t } = useTranslation();
  const { tab = 'groups' } = useParams();
  if (!TABS.some((x) => x.key === tab)) return <Navigate to="/groups/groups" replace />;
  return (
    <div className="page">
      <PageHeader title={t('groupsPage.title')} actions={<ReportScopePicker withProject={false} />} />
      <nav className="tabs" aria-label={t('groupsPage.title')}>
        {TABS.map((x) => (
          <NavLink key={x.key} to={`/groups/${x.key}`}>
            <x.icon size={15} />
            {t(`groupsPage.tabs.${x.key}`)}
          </NavLink>
        ))}
      </nav>
      {tab === 'groups' && <GroupsTab />}
      {tab === 'programs' && <ProgramsTab />}
      {tab === 'scenes' && <ScenesTab />}
      {tab === 'rules' && <RulesTab />}
    </div>
  );
}

export const levelText = (l: number | null | undefined, t: (k: 'groupsPage.off') => string) =>
  l === null || l === undefined ? '—' : l === 0 ? t('groupsPage.off') : `${l} %`;

function GroupsTab() {
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId } = useReportScope();
  const groups = useQuery({
    queryKey: ['groups', clientId],
    queryFn: () => fetchGroups(clientId),
    enabled: !!clientId,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<api.Group | 'new' | null>(null);
  const manage = canAny(me, 'group.manage');
  return (
    <div className="ops-stack">
      <Card
        title={t('groupsPage.groups')}
        bodyClass=""
        actions={
          manage && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!clientId}
              onClick={() => setEditing('new')}
            >
              <Plus size={15} /> {t('groupsPage.newGroup')}
            </button>
          )
        }
      >
        <div className="card-body muted small" style={{ paddingBottom: 0 }}>
          {t('groupsPage.hint')}
        </div>
        <div className="ops-scroll">
          <table className="table table-wrap">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('common.type')}</th>
                <th className="right">{t('groupsPage.devices')}</th>
                <th>{t('groupsPage.runMode')}</th>
                <th>{t('groupsPage.now')}</th>
                <th>{t('groupsPage.next')}</th>
              </tr>
            </thead>
            <tbody>
              {(groups.data?.items ?? []).map((g) => (
                <tr
                  key={g.id}
                  className={`row-click${selected === g.id ? ' row-selected' : ''}`}
                  onClick={() => setSelected(g.id)}
                >
                  <td>
                    {g.color && (
                      <span className="level-swatch" style={{ background: g.color, marginRight: 6 }} />
                    )}
                    <strong>{g.name}</strong>
                  </td>
                  <td>{t(`groupsPage.kind.${g.kind}`)}</td>
                  <td className="num">{g.devices}</td>
                  <td>{t(`groupsPage.mode.${g.run_mode}`)}</td>
                  <td>
                    {levelText(g.now.level, t)}
                    <span className="cell-sub">{t(`groupsPage.source.${g.now.source}`)}</span>
                  </td>
                  <td className="muted small">
                    {g.now.next_at
                      ? `${formatDateTime(g.now.next_at)} → ${levelText(g.now.next_level, t)}`
                      : '—'}
                  </td>
                </tr>
              ))}
              {groups.data?.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    {t('groupsPage.noGroups')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {selected && (
        <GroupDetail id={selected} onEdit={(g) => setEditing(g)} onDeleted={() => setSelected(null)} />
      )}
      {editing && clientId && (
        <GroupModal
          clientId={clientId}
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(id) => setSelected(id)}
        />
      )}
    </div>
  );
}

function GroupModal({
  clientId,
  group,
  onClose,
  onSaved,
}: {
  clientId: string;
  group: api.Group | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const programs = useQuery({ queryKey: ['programs', clientId], queryFn: () => fetchPrograms(clientId) });
  const [f, setF] = useState({
    name: group?.name ?? '',
    kind: group?.kind ?? ('street' as api.GroupKind),
    description: group?.description ?? '',
    color: group?.color ?? '#f59e0b',
    program_id: group?.program_id ?? '',
    run_mode: group?.run_mode ?? ('central' as api.RunMode),
  });
  const save = useMutation({
    mutationFn: () =>
      saveGroup(group?.id ?? null, {
        client_id: clientId,
        name: f.name,
        kind: f.kind,
        description: f.description || null,
        color: f.color,
        program_id: f.program_id || null,
        run_mode: f.run_mode,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['groups'] });
      onSaved(r.id);
      onClose();
    },
  });
  return (
    <Modal title={group ? group.name : t('groupsPage.newGroup')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input
            className="input"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            maxLength={120}
          />
        </Field>
        <Field label={t('common.type')}>
          <select
            className="input"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as api.GroupKind })}
          >
            {GROUP_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`groupsPage.kind.${k}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('groupsPage.program')}>
          <select
            className="input"
            value={f.program_id}
            onChange={(e) => setF({ ...f, program_id: e.target.value })}
          >
            <option value="">{t('groupsPage.noProgram')}</option>
            {(programs.data?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.client_id ? '' : ` (${t('groupsPage.template')})`}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('groupsPage.runMode')} hint={t(`groupsPage.modeHint.${f.run_mode}`)}>
          <select
            className="input"
            value={f.run_mode}
            onChange={(e) => setF({ ...f, run_mode: e.target.value as api.RunMode })}
          >
            {RUN_MODES.map((m) => (
              <option key={m} value={m}>
                {t(`groupsPage.mode.${m}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('groupsPage.color')}>
          <input
            className="input"
            type="color"
            value={f.color}
            onChange={(e) => setF({ ...f, color: e.target.value })}
          />
        </Field>
      </div>
      <Field label={t('projects.description')}>
        <textarea
          className="input textarea"
          rows={2}
          value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
        />
      </Field>
      {save.isError && (
        <p className="form-error">
          {t(`errors.${(save.error as Error).message}` as 'errors.generic', {
            defaultValue: (save.error as Error).message,
          })}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!f.name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}

function GroupDetail({
  id,
  onEdit,
  onDeleted,
}: {
  id: string;
  onEdit: (g: api.Group) => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useMe().data;
  const g = useQuery({ queryKey: ['group', id], queryFn: () => fetchGroup(id) });
  const programs = useQuery({
    queryKey: ['programs', g.data?.client_id],
    queryFn: () => fetchPrograms(g.data!.client_id),
    enabled: !!g.data,
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['group', id] });
    void qc.invalidateQueries({ queryKey: ['groups'] });
  };
  const remove = useMutation({
    mutationFn: () => removeGroupMembers(id, picked),
    onSuccess: () => (setPicked([]), refresh()),
  });
  const del = useMutation({ mutationFn: () => deleteGroup(id), onSuccess: () => (refresh(), onDeleted()) });
  const delRule = useMutation({
    mutationFn: (rule: string) => deleteCalendarRule(id, rule),
    onSuccess: refresh,
  });
  const cancelEv = useMutation({ mutationFn: (ev: string) => cancelGroupEvent(id, ev), onSuccess: refresh });
  if (!g.data) return null;
  const d = g.data;
  const manage = d.can_manage;
  const schedule = canAny(me, 'schedule.manage');
  const pname = (pid: string | null) => programs.data?.items.find((p) => p.id === pid)?.name ?? '—';
  return (
    <>
      <Card
        title={`${d.name} · ${t(`groupsPage.kind.${d.kind}`)}`}
        actions={
          manage && (
            <span className="toolbar">
              <button type="button" className="btn" onClick={() => onEdit(d)}>
                {t('common.edit')}
              </button>
              <button type="button" className="btn btn-danger" onClick={() => del.mutate()}>
                <Trash2 size={14} />
              </button>
            </span>
          )
        }
        bodyClass=""
      >
        <div className="card-body">
          <dl className="kv">
            <dt>{t('groupsPage.program')}</dt>
            <dd>{pname(d.program_id)}</dd>
            <dt>{t('groupsPage.runMode')}</dt>
            <dd>
              {t(`groupsPage.mode.${d.run_mode}`)}
              <span className="cell-sub">{t(`groupsPage.modeHint.${d.run_mode}`)}</span>
            </dd>
            <dt>{t('groupsPage.now')}</dt>
            <dd>
              {levelText(d.now.level, t)} · {t(`groupsPage.source.${d.now.source}`)}
              {d.now.next_at && (
                <span className="cell-sub">
                  {t('groupsPage.nextAt', {
                    at: formatDateTime(d.now.next_at),
                    level: levelText(d.now.next_level, t),
                  })}
                </span>
              )}
            </dd>
            {Object.keys(d.sync).length > 0 && (d.run_mode === 'device' || d.run_mode === 'both') && (
              <>
                <dt>{t('groupsPage.deviceSync')}</dt>
                <dd>
                  {Object.entries(d.sync)
                    .map(
                      ([k, n]) =>
                        `${t(`groupsPage.sync.${k}` as 'groupsPage.sync.none', { defaultValue: k })}: ${n}`,
                    )
                    .join(' · ')}
                  <span className="cell-sub">{t('groupsPage.syncHint')}</span>
                </dd>
              </>
            )}
          </dl>
        </div>
        <div className="card-body toolbar" style={{ paddingTop: 0 }}>
          <strong>{t('groupsPage.members', { n: d.members.length })}</strong>
          <span style={{ flex: 1 }} />
          {manage && picked.length > 0 && (
            <button type="button" className="btn" onClick={() => remove.mutate()}>
              {t('groupsPage.removeSelected', { n: picked.length })}
            </button>
          )}
          {manage && (
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              <Plus size={15} /> {t('groupsPage.addDevices')}
            </button>
          )}
        </div>
        <div className="ops-scroll" style={{ maxHeight: 420 }}>
          <table className="table table-tight table-wrap">
            <thead>
              <tr>
                {manage && <th style={{ width: 32 }} />}
                <th>{t('alarms.device')}</th>
                <th>{t('common.status')}</th>
                <th className="right">{t('groupsPage.dimmer')}</th>
                <th className="right">{t('groupsPage.desired')}</th>
                <th>{t('groupsPage.applied')}</th>
              </tr>
            </thead>
            <tbody>
              {d.members.map((m) => (
                <tr key={m.device_id}>
                  {manage && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={m.name}
                        checked={picked.includes(m.device_id)}
                        onChange={(e) =>
                          setPicked(
                            e.target.checked
                              ? [...picked, m.device_id]
                              : picked.filter((x) => x !== m.device_id),
                          )
                        }
                      />
                    </td>
                  )}
                  <td>
                    <Link to={`/devices/${m.device_id}`}>{m.name}</Link>
                    <span className="cell-sub">{m.site_name}</span>
                  </td>
                  <td>
                    <PresenceBadge presence={m.presence as 'online'} />
                  </td>
                  <td className="num">{m.dimmer_pct ?? '—'}</td>
                  <td className="num">{levelText(m.desired_level, t)}</td>
                  <td>
                    <span
                      className={`pill${m.schedule_state === 'applied' ? ' pill-online' : m.schedule_state === 'failed' ? ' pill-critical' : ''}`}
                    >
                      {t(`groupsPage.state.${m.schedule_state}`)}
                    </span>
                    {m.sync_status && (
                      <span className="cell-sub">
                        {t(`groupsPage.sync.${m.sync_status}` as 'groupsPage.sync.none', {
                          defaultValue: m.sync_status,
                        })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="grid-2">
        <Card title={t('groupsPage.calendar')} bodyClass="">
          <div className="card-body muted small" style={{ paddingBottom: 0 }}>
            {t('groupsPage.calendarHint')}
          </div>
          <table className="table table-tight table-wrap">
            <tbody>
              {d.rules.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.label ?? t(`groupsPage.ruleKind.${r.kind}`)}</strong>
                    <span className="cell-sub">
                      {r.kind === 'weekly' &&
                        (r.weekdays ?? [])
                          .map((w) => t(`groupsPage.weekday.${w}` as 'groupsPage.weekday.0'))
                          .join(', ')}
                      {r.kind === 'date_range' && `${r.date_from} — ${r.date_to}`}
                      {r.kind === 'yearly' && `${r.month_day_from} — ${r.month_day_to}`}
                    </span>
                  </td>
                  <td>{pname(r.program_id)}</td>
                  <td className="num">{r.priority}</td>
                  <td className="right">
                    {schedule && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => delRule.mutate(r.id)}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {schedule && <RuleForm groupId={id} programs={programs.data?.items ?? []} onSaved={refresh} />}
        </Card>
        <Card title={t('groupsPage.events')} bodyClass="">
          <div className="card-body muted small" style={{ paddingBottom: 0 }}>
            {t('groupsPage.eventsHint')}
          </div>
          <table className="table table-tight table-wrap">
            <tbody>
              {d.events.map((e) => (
                <tr key={e.id} style={e.cancelled_at ? { opacity: 0.5 } : undefined}>
                  <td>
                    <strong>
                      {e.label ?? (e.scene_id ? t('groupsPage.sceneEvent') : t('groupsPage.event'))}
                    </strong>
                    <span className="cell-sub">
                      {formatDateTime(e.starts_at)} → {formatDateTime(e.ends_at)}
                    </span>
                  </td>
                  <td>{e.level !== null ? levelText(e.level, t) : pname(e.program_id)}</td>
                  <td className="right">
                    {schedule && !e.cancelled_at && new Date(e.ends_at) > new Date() && (
                      <button type="button" className="btn btn-ghost" onClick={() => cancelEv.mutate(e.id)}>
                        {t('common.cancel')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {schedule && <EventForm groupId={id} programs={programs.data?.items ?? []} onSaved={refresh} />}
        </Card>
      </div>
      {adding && <AddDevicesModal group={d} onClose={() => setAdding(false)} onAdded={refresh} />}
    </>
  );
}

function RuleForm({
  groupId,
  programs,
  onSaved,
}: {
  groupId: string;
  programs: api.Program[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    kind: 'weekly' as api.CalendarRule['kind'],
    program_id: '',
    label: '',
    weekdays: [0, 6],
    date_from: '',
    date_to: '',
    md_from: '12-15',
    md_to: '01-07',
    priority: 10,
  });
  const save = useMutation({
    mutationFn: () =>
      addCalendarRule(groupId, {
        kind: f.kind,
        program_id: f.program_id,
        label: f.label || null,
        priority: f.priority,
        ...(f.kind === 'weekly' ? { weekdays: f.weekdays } : {}),
        ...(f.kind === 'date_range' ? { date_from: f.date_from, date_to: f.date_to } : {}),
        ...(f.kind === 'yearly' ? { month_day_from: f.md_from, month_day_to: f.md_to } : {}),
      }),
    onSuccess: onSaved,
  });
  return (
    <div className="card-body inline-form">
      <Field label={t('groupsPage.ruleKindLabel')}>
        <select
          className="input"
          value={f.kind}
          onChange={(e) => setF({ ...f, kind: e.target.value as typeof f.kind })}
        >
          {(['weekly', 'date_range', 'yearly'] as const).map((k) => (
            <option key={k} value={k}>
              {t(`groupsPage.ruleKind.${k}`)}
            </option>
          ))}
        </select>
      </Field>
      {f.kind === 'weekly' && (
        <div className="field">
          {t('groupsPage.days')}
          <div className="chips">
            {[1, 2, 3, 4, 5, 6, 0].map((w) => (
              <label key={w} className="check-row">
                <input
                  type="checkbox"
                  checked={f.weekdays.includes(w)}
                  onChange={(e) =>
                    setF({
                      ...f,
                      weekdays: e.target.checked ? [...f.weekdays, w] : f.weekdays.filter((x) => x !== w),
                    })
                  }
                />
                {t(`groupsPage.weekday.${w}` as 'groupsPage.weekday.0')}
              </label>
            ))}
          </div>
        </div>
      )}
      {f.kind === 'date_range' && (
        <>
          <Field label={t('groupsPage.from')}>
            <input
              className="input"
              type="date"
              value={f.date_from}
              onChange={(e) => setF({ ...f, date_from: e.target.value })}
            />
          </Field>
          <Field label={t('groupsPage.to')}>
            <input
              className="input"
              type="date"
              value={f.date_to}
              onChange={(e) => setF({ ...f, date_to: e.target.value })}
            />
          </Field>
        </>
      )}
      {f.kind === 'yearly' && (
        <>
          <Field label={t('groupsPage.fromMd')}>
            <input
              className="input"
              value={f.md_from}
              placeholder="MM-DD"
              onChange={(e) => setF({ ...f, md_from: e.target.value })}
            />
          </Field>
          <Field label={t('groupsPage.toMd')}>
            <input
              className="input"
              value={f.md_to}
              placeholder="MM-DD"
              onChange={(e) => setF({ ...f, md_to: e.target.value })}
            />
          </Field>
        </>
      )}
      <Field label={t('groupsPage.program')}>
        <select
          className="input"
          value={f.program_id}
          onChange={(e) => setF({ ...f, program_id: e.target.value })}
        >
          <option value="">—</option>
          {programs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('groupsPage.label')}>
        <input
          className="input"
          value={f.label}
          onChange={(e) => setF({ ...f, label: e.target.value })}
          maxLength={120}
        />
      </Field>
      <Field label={t('groupsPage.priority')}>
        <input
          className="input"
          type="number"
          min={0}
          max={1000}
          value={f.priority}
          onChange={(e) => setF({ ...f, priority: Number(e.target.value) })}
          style={{ width: 80 }}
        />
      </Field>
      <button
        type="button"
        className="btn"
        disabled={!f.program_id || save.isPending}
        onClick={() => save.mutate()}
      >
        <Plus size={14} /> {t('groupsPage.addRule')}
      </button>
      {save.isError && <span className="form-error">{t('errors.INVALID_REQUEST')}</span>}
    </div>
  );
}

const localInput = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

function EventForm({
  groupId,
  programs,
  onSaved,
}: {
  groupId: string;
  programs: api.Program[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const now = new Date();
  const [f, setF] = useState({
    label: '',
    starts: localInput(now),
    ends: localInput(new Date(now.getTime() + 4 * 3_600_000)),
    level: '100',
    program_id: '',
  });
  const save = useMutation({
    mutationFn: () =>
      addGroupEvent(groupId, {
        label: f.label || null,
        starts_at: new Date(f.starts).toISOString(),
        ends_at: new Date(f.ends).toISOString(),
        ...(f.program_id ? { program_id: f.program_id } : { level: Number(f.level) }),
      }),
    onSuccess: onSaved,
  });
  return (
    <div className="card-body inline-form">
      <Field label={t('groupsPage.label')}>
        <input
          className="input"
          value={f.label}
          onChange={(e) => setF({ ...f, label: e.target.value })}
          maxLength={120}
        />
      </Field>
      <Field label={t('groupsPage.from')}>
        <input
          className="input"
          type="datetime-local"
          value={f.starts}
          onChange={(e) => setF({ ...f, starts: e.target.value })}
        />
      </Field>
      <Field label={t('groupsPage.to')}>
        <input
          className="input"
          type="datetime-local"
          value={f.ends}
          onChange={(e) => setF({ ...f, ends: e.target.value })}
        />
      </Field>
      <Field label={t('groupsPage.levelOrProgram')}>
        <select
          className="input"
          value={f.program_id ? `p:${f.program_id}` : f.level}
          onChange={(e) =>
            e.target.value.startsWith('p:')
              ? setF({ ...f, program_id: e.target.value.slice(2) })
              : setF({ ...f, program_id: '', level: e.target.value })
          }
        >
          {[100, 75, 50, 30, 0].map((l) => (
            <option key={l} value={String(l)}>
              {levelText(l, t)}
            </option>
          ))}
          {programs.map((p) => (
            <option key={p.id} value={`p:${p.id}`}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <button type="button" className="btn" disabled={save.isPending} onClick={() => save.mutate()}>
        <Plus size={14} /> {t('groupsPage.addEvent')}
      </button>
      {save.isError && <span className="form-error">{t('errors.INVALID_REQUEST')}</span>}
    </div>
  );
}

function AddDevicesModal({
  group,
  onClose,
  onAdded,
}: {
  group: api.GroupDetail;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const list = useQuery({
    queryKey: ['group-picker', group.client_id, search],
    queryFn: async () => (await getDataSource()).listDevices({ siteId: group.client_id, search, limit: 300 }),
  });
  const members = new Set(group.members.map((m) => m.device_id));
  const add = useMutation({
    mutationFn: () => addGroupMembers(group.id, picked),
    onSuccess: () => {
      onAdded();
      onClose();
    },
  });
  const rows = (list.data?.items ?? []).filter((d) => !members.has(d.id));
  return (
    <Modal title={t('groupsPage.addDevicesTo', { name: group.name })} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('groupsPage.moveHint')}
      </p>
      <div className="toolbar">
        <input
          className="input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
          autoFocus
        />
        <button type="button" className="btn" onClick={() => setPicked(rows.map((d) => d.id))}>
          {t('groupsPage.selectAll', { n: rows.length })}
        </button>
      </div>
      <div className="ops-scroll" style={{ maxHeight: '50vh' }}>
        <table className="table table-tight table-wrap">
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    aria-label={d.name}
                    checked={picked.includes(d.id)}
                    onChange={(e) =>
                      setPicked(e.target.checked ? [...picked, d.id] : picked.filter((x) => x !== d.id))
                    }
                  />
                </td>
                <td>{d.name}</td>
                <td className="muted small">
                  {t(`catalog.deviceType.${d.deviceType}` as 'catalog.deviceType.other', {
                    defaultValue: d.deviceType,
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!picked.length || add.isPending}
          onClick={() => add.mutate()}
        >
          {t('groupsPage.addN', { n: picked.length })}
        </button>
      </div>
    </Modal>
  );
}
